import { Instant, LocalDate, LocalDateTime } from "@js-joda/core";
import assertNever from "assert-never";
import ons from "onsenui";
import React, { useCallback } from "react";
import * as Ons from "react-onsenui";
import { CommCenterTicketCreateParams } from "../../activities/AppNavigator";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import { isReminderRelevantForVisit } from "../../clockInOutUtils";
import useCaregiver from "../../hooks/useCaregiver";
import useClockedInStatus from "../../hooks/useClockedInStatus";
import useMixin from "../../hooks/useMixin";
import usePatientsState from "../../hooks/usePatientsState";
import useStableCallback from "../../hooks/useStableCallback";
import useTrainingCenterState from "../../hooks/useTrainingCenterState";
import { useUploadVerificationSelfieMutation } from "../../hooks/useUploadVerificationSelfieMutation";
import { isLoading, Loading, loading } from "../../Loading";
import {
  ClockInOutCallType,
  CoronaReportAllQuestionsResponse,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
} from "../../messages";
import {
  ChatRoomId,
  ImportedVisitId,
  OnboardingVisitId,
  PatientTaskInstanceId,
  TrainingCenterBundleDueDateId,
  VisitBroadcastId,
  VisitInstanceId,
} from "../../messages/ObjectIds";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { Patient } from "../../system/AppState/AppPatientsState";
import { ChatSystemStrategies } from "../../system/AppState/ChatState";
import { translate } from "../../translate";
import { usePendingPatientTasksBottomSheet } from "../../usePatientTasksBottomSheet";
import { flmap } from "../../utils/fmap";
import {
  changeSchedule,
  decreaseScheduleByMonth,
  increaseScheduleByMonth,
} from "../../utils/handleScheduleDates";
import { HashSet } from "../../utils/HashSet";
import { setStatusBarAsBlackOnWhite } from "../../utils/statusBar";
import { withDefault } from "../../utils/withDefault";
import CaregiverPointsBadge from "../CaregiverPoints/CaregiverPointsBadge";
import { Center } from "../Center";
import ChatMainActivity from "../Chat/ChatMainScreen/ChatMainActivity";
import { ChatsListContainer } from "../ChatsListContainer";
import { getClockActionBannerProps } from "../clockActionBannerUtils";
import useComplianceBannerProps from "../ComplianceBanner/hooks/useComplianceBannerProps";
import FinishOnboardingSection from "../FinishOnboardingSection";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import MonthPicker from "../MonthPicker/MonthPicker";
import PatientsScreen from "../Patients/PatientsScreen";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import SidebarFactory from "../SidebarFactory";
import { TrainingCenterLanguagePreference } from "../TrainingCenter/TrainingCenterLanguageLookup";
import { partitionBundles, transformAgencies } from "../TrainingCenter/TrainingCenterUtils";
import TrainingCenterBundlesList from "../TrainingCenterBundles/components/TrainingCenterBundlesList";
import useContactSupport from "../useContactSupport";
import { VisitsScreen } from "../VisitsScreen";
import ActiveVisitBottomSheetsSubActivity, {
  useActiveVisitBottomSheetsSubActivity,
} from "./ActiveVisitBottomSheetsSubActivity";
import MainActivityChatHeader from "./components/MainActivityChatHeader";
import TrainingCenterExtraHeader from "./components/TrainingCenterExtraHeader";
import VisitsExtraHeader from "./components/VisitsExtraHeader";
import VisitsOptions from "./components/VisitsOptions";
import useMedflytAgencyId from "./hooks/useMedflytAgencyId";
import useTotalNewChatMessagesCount from "./hooks/useTotalNewChatMessagesCount";
import MainScreen from "./MainScreen";
import {
  MainScreenTab,
  MainScreenTabNavigation,
  TrainingCenterTab,
  VisitSort,
  VisitsTab,
} from "./MainTypes";

export interface MainActivityProps {
  api: API;
  appState: AppState;
  languagePreference: TrainingCenterLanguagePreference;
  scrollToScheduleDate?: Instant;
  chatSystemStrategies: ChatSystemStrategies;
  defaultNavigation?: MainScreenTabNavigation;
  activeScreenTab: MainScreenTab;
  hideFinishOnboardingModal: boolean;
  onChangeActiveScreenTab: (screenTab: MainScreenTab) => void;
  getAnomalies: () => Promise<string[]>;
  onRequestNavigate: (page: Page) => void;
  onClickNewChatWithMedflyt: (params: CommCenterTicketCreateParams) => void;
  onClickAvailableVisit: (visitBroadcastId: VisitBroadcastId) => void;
  onClickOnboardingVisit: (onboardingVisitId: OnboardingVisitId) => void;
  onClickAvailableTask: (taskBroadcastId: PatientTaskInstanceId) => void;
  onClickConfirmAssignment: (visit: VisitBroadcastId, isReviewOnly: boolean) => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
}

function MainActivity(props: MainActivityProps) {
  const [isMenuOpen, setMenuOpen] = React.useState(false);
  const [previousScreensStack, setPreviousScreensStack] = React.useState(
    HashSet.empty<MainScreenTab>()
  );

  const [activeVisitsTab, setActiveVisitsTab] = React.useState<VisitsTab>(
    (() => {
      if (props.defaultNavigation?.type === "Visits") {
        return props.defaultNavigation.secondaryTab ?? "Available";
      }
      return "Available";
    })()
  );
  const [activeTrainingCenterTab, setActiveTrainingCenterTab] = React.useState<TrainingCenterTab>(
    (() => {
      if (props.defaultNavigation?.type === "TrainingCenter") {
        return props.defaultNavigation.secondaryTab ?? "Required";
      }
      return "Required";
    })()
  );

  const [isMapOpen, setMapOpen] = React.useState(false);
  const [isVisitSortActionOpen, setVisitSortActionOpen] = React.useState(false);
  const [visitSort, setVisitSort] = React.useState<VisitSort>("DEFAULT");
  const [scheduleYear, setScheduleYear] = React.useState(LocalDate.now().year());
  const [scheduleMonth, setScheduleMonth] = React.useState(LocalDate.now().month());
  const [covidReports, setCovidReports] = React.useState<
    CoronaReportAllQuestionsResponse | Loading
  >(loading());
  const [chatWithOtherAgenciesClicked, setChatWithOtherAgencies] = React.useState(false);

  React.useEffect(setStatusBarAsBlackOnWhite, []);

  React.useEffect(() => {
    props.api.getCoronaReport().then(setCovidReports);
  }, [props.api]);

  const mixin = useMixin(props.appState);
  const trainingCenterState = useTrainingCenterState(props.appState);
  const patientsState = usePatientsState(props.appState);
  const caregiver = useCaregiver(props.appState);
  const clockedInStatus = useClockedInStatus(props.appState);

  const defaultDeviceBackButtonCallback = React.useRef<() => void>();
  const hasMainTabInitiallyChanged = React.useRef(false);

  const contactSupport = useContactSupport({
    api: props.api,
    appState: props.appState,
    onRequestNavigate: props.onRequestNavigate,
    onClickNewChatWithMedflyt: props.onClickNewChatWithMedflyt,
  });

  const handleClickTab = React.useCallback(
    (tab: MainScreenTab) => {
      setPreviousScreensStack(
        previousScreensStack.delete(props.activeScreenTab).add(props.activeScreenTab)
      );

      props.onChangeActiveScreenTab.call(null, tab);
    },
    [previousScreensStack, props.activeScreenTab, props.onChangeActiveScreenTab]
  );

  const handleClickMenu = React.useCallback(() => setMenuOpen(true), []);

  const handleCloseMenu = React.useCallback(() => setMenuOpen(false), []);

  const isNewChatScreen =
    !isLoading(mixin.appCaregiverState) &&
    mixin.appCaregiverState.caregiver.shouldShowNewChat &&
    !chatWithOtherAgenciesClicked;

  const handleRequestNavigate = React.useCallback(
    (page: Page) => {
      props.onRequestNavigate.call(null, page);
      setMenuOpen(false);
    },
    [props.onRequestNavigate]
  );

  const handleViewVisitCard = React.useCallback(
    (visitIds: VisitBroadcastId[]) => {
      // This is run in the background.
      // TODO Should be more robust, retry if it fails...
      props.api.setVisitPreviewed({ visitIds: visitIds, clusterIds: [] });
    },
    [props.api]
  );

  const handleViewVisitMoreDetails = React.useCallback(
    (visitId: VisitBroadcastId) => {
      props.appState.setVisitMoreDetailsViewed(visitId);
    },
    [props.appState]
  );

  const handleClickMap = React.useCallback(() => {
    setMapOpen(!isMapOpen);
  }, [isMapOpen]);

  const handleOpenMap = React.useCallback(() => {
    setMapOpen(true);
  }, []);

  const handleClickVisitSort = React.useCallback(() => {
    setVisitSortActionOpen(!isVisitSortActionOpen);
  }, [isVisitSortActionOpen]);

  const handleChooseVisitSort = React.useCallback((sort: VisitSort | null) => {
    setVisitSort(withDefault(sort, "POSTED_DATE"));
    setVisitSortActionOpen(false);
  }, []);

  const handleClickBundle = React.useCallback(
    (bundleDueDateId: TrainingCenterBundleDueDateId) => {
      handleRequestNavigate({
        type: "TrainingCenterBundle",
        bundleDueDateId: bundleDueDateId,
      });
    },
    [handleRequestNavigate]
  );

  const handleClickNotifications = React.useCallback(() => {
    handleRequestNavigate({ type: "Notifications" });
  }, [handleRequestNavigate]);

  const handleClickVisitInstance = React.useCallback(
    (visitInstanceId: VisitInstanceId) => {
      handleRequestNavigate({ type: "VisitInstance", visitInstanceId: visitInstanceId });
    },
    [handleRequestNavigate]
  );

  const handleClickImportedVisit = React.useCallback(
    (importedVisitId: ImportedVisitId, patientName: string) => {
      handleRequestNavigate({
        type: "ConvertImportedVisit",
        importedVisitId: importedVisitId,
        patientName: patientName,
      });
    },
    [handleRequestNavigate]
  );

  const handleClickTask = React.useCallback(
    (patientTaskInstanceId: PatientTaskInstanceId, initialScheduleDateTimePickerOpen: boolean) => {
      handleRequestNavigate({
        type: "PatientTaskInstance",
        patientTaskInstanceId: patientTaskInstanceId,
        initialScheduleDateTimePickerOpen: initialScheduleDateTimePickerOpen,
      });
    },
    [handleRequestNavigate]
  );

  const handleClickCaseSettings = React.useCallback(() => {
    handleRequestNavigate({ type: "CaseSettings" });
  }, [handleRequestNavigate]);

  const handleClickChatRoom = React.useCallback(
    (chatRoomId: ChatRoomId) => {
      handleRequestNavigate({
        type: "Chat",
        chatRoomId: chatRoomId,
        linkToVisit: true,
      });
    },
    [handleRequestNavigate]
  );

  const handleClickChatPlaceholderButton = React.useCallback(() => {
    handleRequestNavigate({
      type: "MyAgencies",
      addAgency: true,
      resetOnFinish: false,
      popOnNotWorkingWithAgency: false,
    });
  }, [handleRequestNavigate]);

  const handleClickPreviousScheduleMonth = React.useCallback(() => {
    return decreaseScheduleByMonth(scheduleMonth, scheduleYear, setScheduleMonth, setScheduleYear);
  }, [scheduleMonth, scheduleYear]);

  const handleClickNextScheduleMonth = React.useCallback(() => {
    return increaseScheduleByMonth(scheduleMonth, scheduleYear, setScheduleMonth, setScheduleYear);
  }, [scheduleMonth, scheduleYear]);

  const handleChangeScheduleMonth = React.useCallback(
    (newYear: number, newMonth: number) => {
      return changeSchedule(
        scheduleMonth,
        scheduleYear,
        newMonth,
        newYear,
        setScheduleMonth,
        setScheduleYear
      );
    },
    [scheduleMonth, scheduleYear]
  );

  const handleClickCovidQuesionnaire = React.useCallback(
    (reports: CoronaReportAllQuestionsResponse) => {
      return props.onRequestNavigate.call(null, {
        type: "CovidPage",
        coronaReports: reports,
      });
    },
    [props.onRequestNavigate]
  );

  const handleClickPatientCard = React.useCallback(
    (patient: Patient) => {
      return props.onRequestNavigate.call(null, {
        type: "Patient",
        patientId: patient.id,
      });
    },
    [props.onRequestNavigate]
  );

  const totalNewVideos = React.useMemo(() => {
    const count = flmap(trainingCenterState, (x) => x.newVideos);
    return isLoading(count) ? 0 : count;
  }, [trainingCenterState]);

  const agencies = React.useMemo(() => {
    return flmap(mixin.appAgenciesState, (x) => transformAgencies(x.agencies));
  }, [mixin.appAgenciesState]);

  const visitsOptions = React.useMemo(() => {
    return (
      <VisitsOptions
        sort={visitSort}
        isMapOpen={isMapOpen}
        isSortActionOpen={isVisitSortActionOpen}
        onChooseSort={handleChooseVisitSort}
        onClickMap={handleClickMap}
        onClickSort={handleClickVisitSort}
        onClickFilter={handleClickCaseSettings}
      />
    );
  }, [
    handleChooseVisitSort,
    handleClickCaseSettings,
    handleClickMap,
    handleClickVisitSort,
    isMapOpen,
    isVisitSortActionOpen,
    visitSort,
  ]);

  const scheduleOptions = React.useMemo(() => {
    return (
      <MonthPicker
        year={scheduleYear}
        month={scheduleMonth}
        onClickPrevious={handleClickPreviousScheduleMonth}
        onClickNext={handleClickNextScheduleMonth}
        onChange={handleChangeScheduleMonth}
      />
    );
  }, [
    handleChangeScheduleMonth,
    handleClickNextScheduleMonth,
    handleClickPreviousScheduleMonth,
    scheduleMonth,
    scheduleYear,
  ]);

  const extraHeaderVisits = React.useMemo(() => {
    return (
      <VisitsExtraHeader
        showAvailableVisitHeader={!isLoading(caregiver) && !caregiver.isCaregiverOfMedFlyt}
        activeTab={activeVisitsTab}
        availableOptions={visitsOptions}
        scheduleOptions={scheduleOptions}
        setActiveTab={setActiveVisitsTab}
      />
    );
  }, [caregiver, activeVisitsTab, scheduleOptions, visitsOptions]);

  const caregiverRatingHeader = React.useMemo(() => {
    if (isLoading(caregiver) || !caregiver.shouldShowCaregiverRating) {
      return null;
    }

    return (
      <CaregiverPointsBadge
        points={caregiver.points}
        onClick={() => {
          props.onRequestNavigate.call(null, {
            type: "CarebackPoints",
            caregiver: caregiver,
          });
        }}
      />
    );
  }, [caregiver, props.onRequestNavigate]);

  const extraHeaderTrainingCenter = React.useMemo(() => {
    let totalRequired = 0;
    let totalCompleted = 0;

    const partitionedBundles = flmap(trainingCenterState, (x) =>
      partitionBundles(x.bundles.bundles)
    );

    if (!isLoading(partitionedBundles)) {
      totalRequired = partitionedBundles.required.length();
      totalCompleted = partitionedBundles.completed.length();
    }

    return (
      <TrainingCenterExtraHeader
        activeTab={activeTrainingCenterTab}
        totalRequired={totalRequired}
        totalCompleted={totalCompleted}
        onClickTab={setActiveTrainingCenterTab}
      />
    );
  }, [activeTrainingCenterTab, trainingCenterState]);

  const extraHeader = React.useMemo(() => {
    switch (props.activeScreenTab) {
      case "Visits":
        return extraHeaderVisits;
      case "TrainingCenter":
        return extraHeaderTrainingCenter;
      case "Patients":
        return (
          <AppSlideFadeIn>
            <ScaffoldTitle label={translate(STRINGS.LIST_OF_PATIENTS)} />
          </AppSlideFadeIn>
        );
      case "Chat":
        return (
          <AppSlideFadeIn>
            <MainActivityChatHeader
              isChattingWithOtherAgencies={chatWithOtherAgenciesClicked}
              onClickBack={() => setChatWithOtherAgencies(false)}
            />
          </AppSlideFadeIn>
        );
      default:
        assertNever(props.activeScreenTab);
    }
  }, [
    chatWithOtherAgenciesClicked,
    extraHeaderTrainingCenter,
    extraHeaderVisits,
    props.activeScreenTab,
  ]);

  const sidebar = React.useMemo(() => {
    return (
      <SidebarFactory
        caregiver={caregiver}
        caregiverRating={caregiverRatingHeader}
        onRequestNavigate={handleRequestNavigate}
      />
    );
  }, [caregiver, handleRequestNavigate, caregiverRatingHeader]);

  const visitBottomSheetsProps = useActiveVisitBottomSheetsSubActivity({
    api: props.api,
    appState: props.appState,
    contactSupport: contactSupport,
  });

  const openVisitBottomSheetRef = useStableCallback(visitBottomSheetsProps.openVisitBottomSheet);
  const openVisitReminderRef = useStableCallback(
    visitBottomSheetsProps.visitReminderDisclosure.actions.open
  );

  const activeVisitBottomSheets = (
    <ActiveVisitBottomSheetsSubActivity
      api={props.api}
      appState={props.appState}
      contactSupport={contactSupport}
      hookProps={visitBottomSheetsProps}
    />
  );

  const handleRequestAvailableVisits = () => {
    setActiveVisitsTab("Available");
  };

  const handleUpdateActiveVisit = useCallback(
    (visitInstanceId: VisitInstanceId) => {
      if (isLoading(props.appState.scheduledVisits)) {
        return;
      }
      const visitInstance = props.appState.scheduledVisits.scheduledVisitDetails.find(
        (x) => x.id === visitInstanceId
      );
      if (visitInstance === undefined) {
        return;
      }
      openVisitBottomSheetRef({ type: "visit", visit: visitInstance });
    },
    [openVisitBottomSheetRef, props.appState.scheduledVisits]
  );

  const handleClickClockedInBanner = React.useCallback(
    (visitInstanceId: VisitInstanceId) => {
      const presentVisits = flmap(mixin.scheduledVisits, (visit) =>
        visit.scheduledVisitDetails
          .filter((x: VisitInstanceDetails) => isReminderRelevantForVisit(x, LocalDateTime.now()))
          .toArray()
      );
      if (!isLoading(presentVisits) && presentVisits.length > 1) {
        openVisitReminderRef();
      } else {
        handleUpdateActiveVisit(visitInstanceId);
      }
    },
    [handleUpdateActiveVisit, mixin.scheduledVisits, openVisitReminderRef]
  );

  const totalNewChats = useTotalNewChatMessagesCount({
    api: props.api,
    appState: props.appState,
    appChatState: mixin.appChatState,
    chatSystemStrategies: props.chatSystemStrategies,
    isNewChatScreen: isNewChatScreen,
  });

  const trainingCenterScreen = React.useMemo(() => {
    if (isLoading(trainingCenterState) || isLoading(agencies)) {
      return (
        <Center>
          <Ons.ProgressCircular indeterminate={true} />
        </Center>
      );
    }

    if (props.activeScreenTab !== "TrainingCenter") {
      return <></>;
    }

    return (
      <TrainingCenterBundlesList
        activeTab={activeTrainingCenterTab}
        languagePreference={props.languagePreference}
        agencies={agencies}
        bundles={trainingCenterState.bundles.bundles}
        onClickBundle={handleClickBundle}
      />
    );
  }, [
    trainingCenterState,
    agencies,
    props.activeScreenTab,
    props.languagePreference,
    activeTrainingCenterTab,
    handleClickBundle,
  ]);

  const uploadCerificationSelfieMutation = useUploadVerificationSelfieMutation({
    api: props.api,
  });

  const medflytAgencyId = useMedflytAgencyId({
    appAgenciesState: mixin.appAgenciesState,
  });

  const complianceBannerProps = useComplianceBannerProps({
    api: props.api,
    appState: props.appState,
    medflytAgencyId: medflytAgencyId,
    onComplianceBannerClick: () => props.onRequestNavigate({ type: "MyMedflytCompliance" }),
  });

  const visitsScreen = React.useMemo(() => {
    if (
      isLoading(mixin.scheduledVisits) ||
      isLoading(mixin.appVisitsState) ||
      isLoading(mixin.appVisitEditRequestsState) ||
      isLoading(mixin.appAgenciesState) ||
      isLoading(mixin.appTasksState) ||
      isLoading(mixin.appTaskBroadcastsState) ||
      isLoading(clockedInStatus) ||
      isLoading(contactSupport) ||
      isLoading(caregiver)
    ) {
      return (
        <Center>
          <Ons.ProgressCircular indeterminate={true} />
        </Center>
      );
    }

    const clockActionBannerProps = getClockActionBannerProps(
      mixin.scheduledVisits,
      clockedInStatus.status
    );

    const finishOnboardingSection = (
      <FinishOnboardingSection
        caregiver={caregiver}
        getApplicationBanner={() => props.api.getApplicationBanner()}
        hideFinishOnboardingModal={
          props.hideFinishOnboardingModal || activeVisitsTab !== "Available"
        }
        onClick={() => props.onRequestNavigate({ type: "OnboardingActivity" })}
        isClockBannerShown={clockActionBannerProps !== null}
      />
    );

    const handleUploadVerificationSelfie = (
      visitInstanceId: VisitInstanceId,
      imageBase64: string,
      clockType: ClockInOutCallType
    ): Promise<void> => {
      return uploadCerificationSelfieMutation.mutateAsync({
        clockType,
        imageBase64,
        visitInstanceId,
      });
    };

    return (
      <VisitsScreen
        scheduledVisits={mixin.scheduledVisits}
        scrollToScheduleDate={props.scrollToScheduleDate}
        caregiver={caregiver}
        visits={mixin.appVisitsState.visits}
        patientTasks={mixin.appTasksState.tasks}
        patientTaskBroadcasts={mixin.appTaskBroadcastsState.tasks}
        importedVisits={mixin.scheduledVisits.importedVisitDetails}
        agencies={mixin.appAgenciesState.agencies}
        rescheduleRequests={mixin.appVisitEditRequestsState.visitEditRequests}
        clockedInStatus={clockedInStatus}
        activeTab={activeVisitsTab}
        sortBy={visitSort}
        isMapOpen={isMapOpen}
        scheduleMonth={scheduleMonth}
        scheduleYear={scheduleYear}
        contactSupportProps={contactSupport}
        finishOnboardingSection={finishOnboardingSection}
        clockActionBannerProps={clockActionBannerProps}
        complianceBannerProps={complianceBannerProps}
        getAnomalies={props.getAnomalies}
        onOpenMap={handleOpenMap}
        onAvailableTaskClick={props.onClickAvailableTask}
        onAvailableVisitClick={props.onClickAvailableVisit}
        onConfirmAssignmentClick={props.onClickConfirmAssignment}
        onOnboardingVisitClick={props.onClickOnboardingVisit}
        onScheduledVisitClick={handleClickVisitInstance}
        onImportedVisitClick={handleClickImportedVisit}
        onClickTask={handleClickTask}
        onCaseSettingsNavigationClick={handleClickCaseSettings}
        onVisitCardViewed={handleViewVisitCard}
        onVisitMoreDetailsViewed={handleViewVisitMoreDetails}
        onClock={props.onClock}
        onClickTab={setActiveVisitsTab}
        onClickClockedInBanner={handleClickClockedInBanner}
        onRequestNavigate={props.onRequestNavigate}
        onClickStartNow={openVisitBottomSheetRef}
        onRequestAvailableVisits={handleRequestAvailableVisits}
        onUploadVerificationSelfie={handleUploadVerificationSelfie}
        onRequestVisitAdjustmentCheck={props.api.getCaregiverCanAdjustVisitInstanceTimes}
        onClickNextMonth={handleClickNextScheduleMonth}
        onCheckIfDocumentsSubmitted={props.api.checkIfAllDocumentsSubmitted}
        onCaregiverMarkVisitAsMissed={props.api.caregiverMarkVisitAsMissed}
      />
    );
  }, [
    mixin.scheduledVisits,
    mixin.appVisitsState,
    mixin.appVisitEditRequestsState,
    mixin.appAgenciesState,
    mixin.appTasksState,
    mixin.appTaskBroadcastsState,
    clockedInStatus,
    contactSupport,
    caregiver,
    props,
    activeVisitsTab,
    visitSort,
    isMapOpen,
    scheduleMonth,
    scheduleYear,
    complianceBannerProps,
    handleOpenMap,
    handleClickVisitInstance,
    handleClickImportedVisit,
    handleClickTask,
    handleClickCaseSettings,
    handleViewVisitCard,
    handleViewVisitMoreDetails,
    handleClickClockedInBanner,
    openVisitBottomSheetRef,
    handleClickNextScheduleMonth,
    uploadCerificationSelfieMutation,
  ]);

  const patientsScreen = React.useMemo(() => {
    if (isLoading(patientsState)) {
      return (
        <Center>
          <Ons.ProgressCircular indeterminate={true} />
        </Center>
      );
    }

    if (props.activeScreenTab !== "Patients") {
      return <></>;
    }

    return (
      <PatientsScreen
        isSearch={false}
        patients={patientsState.patients}
        onClickPatient={handleClickPatientCard}
        onRequestAvailableVisits={handleRequestAvailableVisits}
      />
    );
  }, [patientsState, props.activeScreenTab, handleClickPatientCard]);

  const chatScreen = React.useMemo(() => {
    if (
      isLoading(mixin.appVisitsState) ||
      isLoading(mixin.appAgenciesState) ||
      isLoading(mixin.appCaregiverState)
    ) {
      return (
        <Center>
          <Ons.ProgressCircular indeterminate={true} />
        </Center>
      );
    }

    if (props.activeScreenTab !== "Chat") {
      return <></>;
    }

    if (isNewChatScreen) {
      return (
        <ChatMainActivity
          api={props.api}
          caregiver={mixin.appCaregiverState.caregiver}
          onRequestNavigate={props.onRequestNavigate}
          onClickChatWithOtherAgencies={() => setChatWithOtherAgencies(true)}
        />
      );
    }

    return (
      <ChatsListContainer
        chats={mixin.appChatState}
        chatSystemStrategies={props.chatSystemStrategies}
        agencies={mixin.appAgenciesState}
        visits={mixin.appVisitsState.visits}
        onChatRoomClick={handleClickChatRoom}
        onPlaceHolderBtnClick={handleClickChatPlaceholderButton}
      />
    );
  }, [
    handleClickChatPlaceholderButton,
    handleClickChatRoom,
    isNewChatScreen,
    mixin.appAgenciesState,
    mixin.appCaregiverState,
    mixin.appChatState,
    mixin.appVisitsState,
    props.activeScreenTab,
    props.api,
    props.chatSystemStrategies,
    props.onRequestNavigate,
  ]);

  React.useEffect(() => {
    if (hasMainTabInitiallyChanged.current) {
      return;
    } else {
      hasMainTabInitiallyChanged.current = true;
    }

    if (
      isLoading(mixin.scheduledVisits) ||
      isLoading(mixin.appVisitsState) ||
      isLoading(mixin.appVisitEditRequestsState) ||
      isLoading(mixin.appAgenciesState) ||
      isLoading(mixin.appTasksState) ||
      isLoading(trainingCenterState)
    ) {
      return;
    }

    if (
      mixin.appVisitsState.visits.length() > 0 ||
      mixin.scheduledVisits.importedVisitDetails.length() > 0
    ) {
      props.onChangeActiveScreenTab.call(null, "Visits");
      return;
    }

    if (trainingCenterState.bundles.bundles.length() > 0) {
      props.onChangeActiveScreenTab.call(null, "TrainingCenter");
    }
  }, [
    mixin.scheduledVisits,
    mixin.appVisitsState,
    mixin.appTasksState,
    mixin.appVisitEditRequestsState,
    mixin.appAgenciesState,
    trainingCenterState,
    props.onChangeActiveScreenTab,
  ]);

  const {
    totalPendingPatientTasks,
    handleRequestClosePendingPatientTasksBottomSheet,
    handleClickReviewTasks,
    isPendingPatientTasksBottomSheetOpen,
  } = usePendingPatientTasksBottomSheet({
    tasksState: mixin.appTasksState,
    onClickReviewTasks: (nextPendingTaskDate: LocalDate) => {
      setActiveVisitsTab("Schedule");
      setScheduleYear(nextPendingTaskDate.year());
      setScheduleMonth(nextPendingTaskDate.month());
    },
  });

  /**
   * Overrides the default device back button by firstly going back through screen tabs and only
   * after emptying the stack, restore the default callback. The reason I put the callback
   * inside useRef was because we can get the default callback only after `ons.ready` has been resolved.
   * If you have a better approach, feel free to refactor it.
   */
  React.useEffect(() => {
    if ((ons as any)._defaultDeviceBackButtonHandler === undefined) {
      return;
    }

    if (defaultDeviceBackButtonCallback.current === undefined) {
      defaultDeviceBackButtonCallback.current = (
        ons as any
      )._defaultDeviceBackButtonHandler._callback;
    }

    ons.ready(() => {
      if (previousScreensStack.size() > 0) {
        ons.setDefaultDeviceBackButtonListener(() => {
          const lastScreenTab = previousScreensStack.last();
          const newStack = previousScreensStack.delete(lastScreenTab);
          setPreviousScreensStack(newStack);
          props.onChangeActiveScreenTab.call(null, lastScreenTab);
        });
      } else {
        if (defaultDeviceBackButtonCallback.current !== undefined) {
          ons.setDefaultDeviceBackButtonListener(defaultDeviceBackButtonCallback.current);
        }
      }
    });

    return () =>
      ons.ready(() => {
        if (defaultDeviceBackButtonCallback.current !== undefined) {
          ons.setDefaultDeviceBackButtonListener(defaultDeviceBackButtonCallback.current);
        }
      });
  }, [previousScreensStack, props.onChangeActiveScreenTab]);

  return (
    <>
      <MainScreen
        activeScreenTab={props.activeScreenTab}
        visitsView={visitsScreen}
        trainingCenterView={trainingCenterScreen}
        patientsView={patientsScreen}
        chatView={chatScreen}
        extraHeader={extraHeader}
        sidebar={sidebar}
        covidResponse={covidReports}
        totalNewVideos={totalNewVideos}
        totalNewChats={totalNewChats}
        totalNewNotifications={mixin.notificationsState
          .getNotificationsDB()
          .getNumUnviewedNotifications()}
        pendingTasksAmount={totalPendingPatientTasks}
        isOpenPendingPatientTasksBottomSheet={isPendingPatientTasksBottomSheetOpen}
        onRequestClosePendingPatientTasksBottomSheet={
          handleRequestClosePendingPatientTasksBottomSheet
        }
        onClickReviewTasks={handleClickReviewTasks}
        isMenuOpen={isMenuOpen}
        isCaregiverOfMedFlyt={isLoading(caregiver) ? false : caregiver.isCaregiverOfMedFlyt}
        isInstapayVisible={isLoading(caregiver) ? false : caregiver.isInstapayVisible}
        onClickTab={handleClickTab}
        onClickNotifications={handleClickNotifications}
        onClickCovidQuestionnaire={handleClickCovidQuesionnaire}
        onClickMenu={handleClickMenu}
        onCloseMenu={handleCloseMenu}
        startActions={caregiverRatingHeader}
        onRequestNavigate={props.onRequestNavigate}
      >
        {activeVisitBottomSheets}
      </MainScreen>
    </>
  );
}

export default React.memo(MainActivity);
